import { pdf } from '@react-pdf/renderer';
import type { DocumentProps } from '@react-pdf/renderer';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

type GeneratedURL =
  | {
      url: string;
      valid: true;
    }
  | {
      url?: undefined;
      valid: false;
    };

type PreviewMetadata = {
  document: React.ReactElement<DocumentProps>;
  filename: string;
  product: string;
};

export const useGeneratePreviewURL = ({
  document,
  filename,
  product
}: Partial<PreviewMetadata>) => {
  const [previewURL, setPreviewURL] = useState<GeneratedURL>({ valid: false });

  useEffect(() => {
    if (!document || !filename || !product) {
      return setPreviewURL({ valid: false });
    }

    pdf(document)
      .toBlob()
      .then(URL.createObjectURL)
      .then((url) => ({ url, filename, product }) as DecodedMetadata)
      .then((metadata) => btoa(JSON.stringify(metadata)))
      .then((encodedUrl) => `/preview/${encodedUrl}`)
      .then((url) => setPreviewURL({ url, valid: true }))
      .catch(() => setPreviewURL({ valid: false }));
  }, [document]);

  return previewURL;
};

type DecodedMetadata = {
  url: string;
  filename: string;
  product: string;
};

type MetadataResult =
  | {
      data: DecodedMetadata;
      error?: false;
    }
  | {
      data?: undefined;
      error: true;
    };

const PREVIEW_URL_PROTOCOL = 'blob:';

const isDecodedMetadata = (value: unknown): value is DecodedMetadata => {
  const { url, filename, product } = (value ?? {}) as Partial<DecodedMetadata>;

  return (
    typeof url === 'string' &&
    typeof filename === 'string' &&
    typeof product === 'string'
  );
};

const BLOB_ORIGIN_PROTOCOLS = ['http:', 'https:'];

const hasAllowedProtocol = (url: string) => {
  try {
    const { protocol, pathname } = new URL(url);

    if (protocol !== PREVIEW_URL_PROTOCOL) {
      return false;
    }

    return BLOB_ORIGIN_PROTOCOLS.includes(new URL(pathname).protocol);
  } catch {
    return false;
  }
};

export const useDecodedPreviewURL = () => {
  const { previewMetadata } = useParams();

  const metadata: MetadataResult = useMemo(() => {
    if (!previewMetadata) {
      return { error: true };
    }

    try {
      const data: unknown = JSON.parse(atob(previewMetadata));

      if (!isDecodedMetadata(data) || !hasAllowedProtocol(data.url)) {
        return { error: true };
      }

      return { data };
    } catch {
      return { error: true };
    }
  }, [previewMetadata]);

  return metadata;
};
